import type { AvailableWarehouseLotDTO, WarehouseLotDTO } from "../../../client"

export const formatMoney = (value?: number | null) =>
  typeof value === "number" ? value.toFixed(2) : "0.00"

export const formatQty = (value?: number | null) =>
  typeof value === "number" ? value.toFixed(3).replace(/\.?0+$/, "") : "0"

/** Formats an API date without shifting its calendar day to the browser timezone. */
export const formatLotDate = (value?: string | null) => {
  if (!value) return ""
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return value
  return `${match[3]}.${match[2]}.${match[1]}`
}

export const buildLotLabel = (lot: {
  lot_no: string
  unit_cost_netto: number
  qty_available?: number
  qty_remaining?: number
  location_code?: string
  production_date?: string
  expiration_date?: string
}) => {
  const qty = lot.qty_available ?? lot.qty_remaining ?? 0
  const locationSuffix = lot.location_code ? ` • ${lot.location_code}` : ""
  const productionDateSuffix = lot.production_date
    ? ` • Prod. ${formatLotDate(lot.production_date)}`
    : ""
  const expirationDateSuffix = lot.expiration_date
    ? ` • Ważne do ${formatLotDate(lot.expiration_date)}`
    : ""
  return `${lot.lot_no} • ${formatMoney(lot.unit_cost_netto)} netto • ${formatQty(qty)} szt${locationSuffix}${productionDateSuffix}${expirationDateSuffix}`
}

export const buildLotDatesLabel = (lot: {
  production_date?: string | null
  expiration_date?: string | null
}) => {
  const parts: string[] = []
  if (lot.production_date) {
    parts.push(`Prod. ${formatLotDate(lot.production_date)}`)
  }
  if (lot.expiration_date) {
    parts.push(`Ważne do ${formatLotDate(lot.expiration_date)}`)
  }
  return parts.join("\n") || "—"
}

export const buildFinishedGoodsSummary = (lots: WarehouseLotDTO[]) => {
  const grouped = new Map<number, number>()

  for (const lot of lots) {
    const qty = Number(lot.qty_remaining ?? 0)
    if (qty <= 0) continue
    const cost = Number(lot.unit_cost_netto ?? 0)
    grouped.set(cost, (grouped.get(cost) ?? 0) + qty)
  }

  return Array.from(grouped.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([cost, qty]) => `${formatQty(qty)} szt po ${formatMoney(cost)}`)
    .join(", ")
}

export const getLotAvailabilityStatus = (
  lot: AvailableWarehouseLotDTO | WarehouseLotDTO,
) => {
  const qty =
    "qty_available" in lot
      ? Number(lot.qty_available ?? 0)
      : Number(lot.qty_remaining ?? 0)

  if (qty <= 0) return "empty"
  if (qty < 1) return "low"
  return "available"
}
